import type { ParsedUrlQuery } from 'querystring'
import type { GetServerSideProps } from 'next'

import { getWalk } from 'lib/content'
import sanitizeKalturaUrl from 'lib/sanitizeKalturaUrl'
import { createSaveProgressParams } from 'lib/hooks/api/useSaveProgress'
import MapInterstitial, {
  MapInterstitialProps,
} from 'components/presentation/MapInterstitial'
import { createNavBarProps } from 'components/NavBar'

const PageComponent = MapInterstitial

interface PageParams extends ParsedUrlQuery {
  walk: string
  completedStop: string // n.b. one-based index
}

export const getServerSideProps: GetServerSideProps<
  MapInterstitialProps,
  PageParams
> = async ({ params, locale = 'en' }) => {
  if (!params) {
    throw new Error('No parameters passed to getServerSideProps')
  }

  const walk = await getWalk(params.walk, locale)

  if (walk == null) {
    return { notFound: true }
  }

  const completedStop = Number(params.completedStop)

  const stops = walk.stops.map(
    ({ image_url, name, lat, lng, slug }, stopIndex) => ({
      imageUrl: sanitizeKalturaUrl(image_url, 200),
      name: name[locale] ?? '',
      lat,
      lng,
      slug,
      completed: stopIndex < completedStop,
    })
  )

  const nextStop = getNextStop(completedStop, walk.stops.length)

  if (nextStop === -1) {
    return { notFound: true }
  }

  const props = {
    walkSlug: params.walk,
    walkName: walk.name[locale] ?? '',
    nextStop,
    stops,
    navBarProps: createNavBarProps(
      walk,
      {
        walk: params.walk,
        completedStop,
      },
      locale
    ),
    saveProgressParams: createSaveProgressParams(
      walk,
      walk.stops[completedStop - 1]
    ),
  }

  return {
    props,
  }
}

const getNextStop = (nextStop: number, stopCount: number) => {
  if (nextStop >= 1 || nextStop <= stopCount) {
    // finished last stop (n.b. nextStop is one-indexed)
    if (nextStop === stopCount) {
      return null
    }

    return nextStop
  }

  return -1
}

export default PageComponent
